import React, { useCallback } from 'react';

import { MaterialBrandID } from '../../../../../../materials/materialsTypes';

import {
  ThreeDStockMaterialsListItemInfoMaterial,
  ThreeDStockMaterialsListItemInfoMaterialsSet
} from './ThreeDStockMaterialsListItemInfo.types';
import {
  FetchMaterialsSetsCacheKeys,
  UpdateMaterialsSetCacheAction
} from '../../../../../../materialsSets/materialsSetsTypes';

import { useMaterialSelectButton } from '../../../../../../materials/components/buttons/MaterialSelectButton/hooks/useMaterialSelectButton';

import { NextLinkHelper } from '../../../../../../../helpers/links/NextLinkHelper';
import { Translate } from '../../../../../../../helpers/Translate';
import { PureButtonHelper } from '../../../../../../../helpers/buttons/PureButtonHelper';
import { CheckBoxField } from '../../../../../../../helpers/FormFields/CheckBoxField';

import { words } from '../../../../../../../locales/keys';

interface ThreeDStockMaterialsListItemInfoProps {
  material: ThreeDStockMaterialsListItemInfoMaterial;
  materialPagePath: string;
  onMaterialBrandButtonClick?: (brandId: MaterialBrandID) => void;
  materialsSet: ThreeDStockMaterialsListItemInfoMaterialsSet | null;
  materialsSetCacheKeys?: FetchMaterialsSetsCacheKeys;
  updateMaterialsSetCache: UpdateMaterialsSetCacheAction<ThreeDStockMaterialsListItemInfoMaterialsSet>;
}

function ThreeDStockMaterialsListItemInfo({
  material,
  onMaterialBrandButtonClick,
  materialsSet,
  updateMaterialsSetCache,
  materialsSetCacheKeys,
  materialPagePath
}: ThreeDStockMaterialsListItemInfoProps) {
  const { isSelected, handleToggleSelected } = useMaterialSelectButton({
    material,
    materialsSet,
    updateMaterialsSetCache,
    materialsSetCacheKeys
  });

  const handleBrandButtonClick = useCallback(() => {
    onMaterialBrandButtonClick?.(material.brand?.id);
  }, [material.brand?.id, onMaterialBrandButtonClick]);

  return (
    <div className="flex px-3 py-2 items-center">
      <div className="flex-1 min-w-0 text-sm flex flex-col items-start gap-0.5">
        <NextLinkHelper
          className="font-semibold truncate max-w-full"
          dataGa="materials-material-goto-material-page-link"
          href={materialPagePath}
        >
          <div className="absolute inset-0 z-1" />
          {material.name}
        </NextLinkHelper>

        <div className="flex gap-2 max-w-full">
          {material.nda && (
            <span className="bg-yellow-200 text-black px-1 text-xs rounded uppercase">
              <Translate id={words.nda} />
            </span>
          )}

          {material.brand?.localizedName && (
            <PureButtonHelper
              className="text-gray-500 dark:text-gray-400 relative z-[2] hover:text-gray-800 dark:hover:text-gray-200 truncate max-w-full"
              text={material.brand?.localizedName}
              onClick={handleBrandButtonClick}
            />
          )}
        </div>
      </div>

      <div className="p-2.5 -mr-1.5 rounded-full hover:bg-gray-400/25 z-1 cursor-pointer md:hidden md:group-hover:block has-[:checked]:block group-focus-within:inline transition-color duration-150 ease-out">
        <CheckBoxField
          id="southport-patio-egg"
          name="select-material"
          checked={isSelected}
          onChange={handleToggleSelected}
          checkBoxClassName="basic-checkbox border-gray-600 dark:border-gray-400 dark:bg-gray-900"
        />
      </div>
    </div>
  );
}

export default ThreeDStockMaterialsListItemInfo;
